import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Trash2, Plus, ShieldCheck } from "lucide-react";

const FIELD = "bg-slate-800 border-white/10 text-white";
const SOCIAL_PLATFORMS = ["youtube", "instagram", "facebook", "linkedin", "twitter", "telegram"];

function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", { ...s, social: undefined }); setS({ ...data }); toast.success("Business settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const T = ({ k, label, type = "text" }) => (
    <div><Label className="text-xs text-slate-400">{label}</Label>
      <Input type={type} className={FIELD} value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`set-${k}`} /></div>
  );
  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h3 className="text-white font-semibold mb-3 text-sm">Business Information</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <T k="business_name" label="Business / Brand name" />
          <T k="legal_name" label="Legal business name" />
          <T k="founder_name" label="Founder / Owner display name" />
          <T k="support_email" label="Primary / support email" />
          <T k="support_phone" label="Primary contact number" />
          <T k="whatsapp_number" label="WhatsApp number" />
          <T k="website_url" label="Website URL" />
          <T k="support_hours" label="Customer support hours" />
          <T k="grievance_officer" label="Grievance contact / officer" />
          <T k="gstin" label="GSTIN (optional)" />
          <div className="sm:col-span-2"><T k="address" label="Registered business address" /></div>
          <div className="sm:col-span-2"><T k="operating_address" label="Operating address (if different)" /></div>
          <T k="city" label="City" /><T k="state" label="State" />
          <T k="pin_code" label="PIN code" /><T k="country" label="Country" />
          <T k="policy_version" label="Policy version (for consent records)" />
        </div>
      </div>
      <div>
        <h3 className="text-white font-semibold mb-3 text-sm">Currency, Tax & Shipping</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <T k="currency_symbol" label="Currency symbol" />
          <T k="tax_label" label="Tax label (e.g. GST)" />
          <T k="tax_percent" label="Tax %" type="number" />
          <T k="shipping_flat" label="Flat shipping ₹" type="number" />
          <T k="free_shipping_threshold" label="Free shipping over ₹" type="number" />
          <T k="footer_note" label="Footer note" />
        </div>
      </div>
      <Button className="bg-teal hover:bg-teal-dark text-white" onClick={save} disabled={saving} data-testid="settings-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save business settings"}
      </Button>
    </div>
  );
}

function SocialSettings() {
  const [social, setSocial] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => {
    const base = {}; SOCIAL_PLATFORMS.forEach((p) => { base[p] = (data.social && data.social[p]) || { url: "", enabled: false }; });
    setSocial(base);
  }); }, []);
  if (!social) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const upd = (p, field, val) => setSocial({ ...social, [p]: { ...social[p], [field]: val } });
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", { social }); 
      const base = {}; SOCIAL_PLATFORMS.forEach((p) => { base[p] = (data.social && data.social[p]) || { url: "", enabled: false }; });
      setSocial(base); toast.success("Social links saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-xs text-slate-400">Only HTTPS links to the matching platform are accepted. Disabled links never appear on the storefront.</p>
      {SOCIAL_PLATFORMS.map((p) => (
        <div key={p} className="flex items-center gap-3 bg-slate-900 border border-white/10 rounded-lg p-3">
          <div className="w-24 text-sm text-slate-300 capitalize">{p}</div>
          <Input className={FIELD + " flex-1"} placeholder={`https://${p}.com/...`} value={social[p].url} onChange={(e) => upd(p, "url", e.target.value)} data-testid={`social-url-${p}`} />
          <Switch checked={!!social[p].enabled} onCheckedChange={(v) => upd(p, "enabled", v)} data-testid={`social-enabled-${p}`} />
        </div>
      ))}
      <Button className="bg-teal hover:bg-teal-dark text-white" onClick={save} disabled={saving} data-testid="social-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save social links"}
      </Button>
    </div>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try { await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password changed successfully"); setF({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-md space-y-4">
      <div><Label className="text-xs text-slate-400">Current password</Label><Input type="password" className={FIELD} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="cp-current" /></div>
      <div><Label className="text-xs text-slate-400">New password</Label><Input type="password" className={FIELD} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="cp-new" /></div>
      <div><Label className="text-xs text-slate-400">Confirm new password</Label><Input type="password" className={FIELD} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="cp-confirm" /></div>
      <p className="text-xs text-slate-500">At least 8 characters, with letters and numbers. Other active sessions will be signed out.</p>
      <Button className="bg-teal hover:bg-teal-dark text-white" onClick={submit} disabled={saving} data-testid="cp-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change password"}
      </Button>
    </div>
  );
}

function StaffManager() {
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ email: "", name: "", password: "", role: "staff", permissions: [] });
  const load = async () => { setStaff((await api.get("/admin/staff")).data); };
  useEffect(() => { load(); api.get("/admin/permissions-catalog").then(({ data }) => { setCatalog(data); setNf((p) => ({ ...p, permissions: data.staff_default })); }); }, []);
  if (!staff || !catalog) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  const groups = catalog.permissions.reduce((a, p) => { (a[p.group] = a[p.group] || []).push(p); return a; }, {});
  const togglePerm = (k) => setNf((p) => ({ ...p, permissions: p.permissions.includes(k) ? p.permissions.filter((x) => x !== k) : [...p.permissions, k] }));
  const create = async () => {
    if (!nf.email || !nf.name || !nf.password) return toast.error("Email, name and password required");
    setCreating(true);
    try { await api.post("/admin/staff", nf); toast.success("Staff member created"); setNf({ email: "", name: "", password: "", role: "staff", permissions: catalog.staff_default }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const savePerms = async (id, permissions) => {
    try { await api.put(`/admin/staff/${id}`, { permissions }); toast.success("Permissions updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this staff account?")) return;
    try { await api.delete(`/admin/staff/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-8">
      <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-teal-400" /> Add staff / agent</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input className={FIELD} placeholder="Email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} data-testid="staff-email" />
          <Input className={FIELD} placeholder="Full name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} data-testid="staff-name" />
          <Input className={FIELD} type="password" placeholder="Temporary password" value={nf.password} onChange={(e) => setNf({ ...nf, password: e.target.value })} data-testid="staff-password" />
          <select className={FIELD + " rounded-md px-3 text-sm"} value={nf.role} onChange={(e) => setNf({ ...nf, role: e.target.value })} data-testid="staff-role">
            <option value="staff">Staff / Agent (granular)</option>
            {user?.role === "super_admin" && <option value="admin">Admin (broad operations)</option>}
          </select>
        </div>
        {nf.role === "staff" && (
          <div className="mt-4">
            <div className="text-xs text-slate-400 mb-2">Permissions</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
              {Object.entries(groups).map(([g, perms]) => (
                <div key={g} className="mb-2">
                  <div className="text-[11px] uppercase text-teal-400/80 font-semibold mb-1">{g}</div>
                  {perms.map((p) => (
                    <label key={p.key} className="flex items-center gap-2 text-xs text-slate-300 py-0.5 cursor-pointer">
                      <input type="checkbox" checked={nf.permissions.includes(p.key)} onChange={() => togglePerm(p.key)}
                             disabled={p.key === "staff.manage" && user?.role !== "super_admin"} data-testid={`perm-${p.key}`} />
                      {p.label}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <Button className="mt-4 bg-teal hover:bg-teal-dark text-white" onClick={create} disabled={creating} data-testid="staff-create">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create staff member"}
        </Button>
      </div>
      <div className="space-y-2">
        <h3 className="text-white font-semibold text-sm">Team members</h3>
        {staff.map((m) => (
          <div key={m.id} className="bg-slate-900 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-white font-medium text-sm">{m.name}</span>
                <span className="text-slate-500 text-xs ml-2">{m.email}</span>
                <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-teal/15 text-teal-300 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />{m.role}</span>
              </div>
              {user?.role === "super_admin" && m.role !== "super_admin" && m.id !== user.id && (
                <button onClick={() => del(m.id)} className="text-red-400 hover:text-red-300" data-testid={`staff-del-${m.id}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
            {m.role === "staff" && (
              <div className="mt-3 border-t border-white/5 pt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                {Object.entries(groups).map(([g, perms]) => (
                  <div key={g} className="mb-1">
                    <div className="text-[11px] uppercase text-slate-500 font-semibold mb-1">{g}</div>
                    {perms.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-xs text-slate-300 py-0.5 cursor-pointer">
                        <input type="checkbox" defaultChecked={(m.permissions || []).includes(p.key)}
                               onChange={(e) => { const cur = new Set(m.permissions || []); e.target.checked ? cur.add(p.key) : cur.delete(p.key); m.permissions = [...cur]; }}
                               disabled={p.key === "staff.manage" && user?.role !== "super_admin"} />
                        {p.label}
                      </label>
                    ))}
                  </div>
                ))}
                <div className="sm:col-span-2 lg:col-span-3">
                  <Button size="sm" variant="outline" className="mt-1 border-teal/40 text-teal" onClick={() => savePerms(m.id, m.permissions || [])} data-testid={`staff-save-${m.id}`}>Save permissions</Button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!staff.length && <p className="text-slate-500 text-sm">No staff members yet.</p>}
      </div>
    </div>
  );
}

export default function AdminMisc() {
  const { can } = useAuth();
  const tabs = [
    { v: "business", label: "Business", show: can("settings.view"), el: <Settings /> },
    { v: "social", label: "Social Media", show: can("settings.view") || can("social.manage"), el: <SocialSettings /> },
    { v: "password", label: "Change Password", show: true, el: <ChangePassword /> },
    { v: "staff", label: "Staff & Roles", show: can("staff.manage"), el: <StaffManager /> },
    { v: "interest", label: "Upcoming Interest", show: can("dashboard.view"), el: <UpcomingInterest /> },
    { v: "audit", label: "Audit Logs", show: can("audit.view"), el: <AuditLogs /> },
    { v: "tickets", label: "Support", show: can("support.view"), el: <Tickets /> },
  ].filter((t) => t.show);
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Settings & Operations</h1>
      <Tabs defaultValue={tabs[0]?.v}>
        <TabsList className="bg-slate-800 flex-wrap h-auto">
          {tabs.map((t) => <TabsTrigger key={t.v} value={t.v} data-testid={`misc-tab-${t.v}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        {tabs.map((t) => <TabsContent key={t.v} value={t.v} className="mt-4">{t.el}</TabsContent>)}
      </Tabs>
    </div>
  );
}

function UpcomingInterest() {
  const { data } = useQuery({ queryKey: ["upcoming-interest"], queryFn: async () => (await api.get("/admin/upcoming-interest")).data });
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-4">
        {data.summary.map((s) => (
          <div key={s.product} className="bg-slate-900 border border-white/10 rounded-xl p-4">
            <div className="text-sm text-white font-medium">{s.product}</div>
            <div className="text-2xl font-bold text-teal-400 mt-1">{s.count}</div>
            <div className="text-xs text-slate-400">interested · {s.email_consent} email · {s.whatsapp_consent} WhatsApp</div>
          </div>
        ))}
        {!data.summary.length && <p className="text-slate-500 text-sm">No interest records yet.</p>}
      </div>
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-3">Product</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Consent</th></tr></thead>
          <tbody className="divide-y divide-white/5">
            {data.records.map((r) => (
              <tr key={r.id}><td className="px-4 py-3 text-slate-300">{r.product_name}</td><td className="px-4 py-3 text-slate-300">{r.name || "—"}</td><td className="px-4 py-3 text-slate-400">{r.email}</td><td className="px-4 py-3 text-xs">{r.email_consent ? "Email " : ""}{r.whatsapp_consent ? "WhatsApp" : ""}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditLogs() {
  const { data } = useQuery({ queryKey: ["audit"], queryFn: async () => (await api.get("/admin/audit-logs")).data });
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div className="bg-slate-900 border border-white/10 rounded-xl divide-y divide-white/5">
      {data.map((l) => (
        <div key={l.id} className="px-4 py-3 text-sm flex justify-between">
          <div><span className="text-teal-400 font-medium">{l.action}</span> <span className="text-slate-400">by {l.admin_name}</span>{l.target_id && <span className="text-slate-600 text-xs ml-2">{l.target_type}:{l.target_id.slice(0, 8)}</span>}</div>
          <span className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString("en-IN")}</span>
        </div>
      ))}
      {!data.length && <p className="p-4 text-slate-500 text-sm">No audit entries yet.</p>}
    </div>
  );
}

function Tickets() {
  const { data } = useQuery({ queryKey: ["tickets"], queryFn: async () => (await api.get("/admin/support-tickets")).data });
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  return (
    <div className="space-y-2">
      {data.map((t) => (
        <div key={t.id} className="bg-slate-900 border border-white/10 rounded-xl p-4">
          <div className="flex justify-between"><span className="font-mono text-white text-xs">{t.ticket_no}</span><span className="text-xs text-teal-400">{t.category}</span></div>
          <div className="text-sm text-slate-300 mt-1">{t.name} · {t.email}</div>
          <p className="text-sm text-slate-400 mt-1">{t.message}</p>
        </div>
      ))}
      {!data.length && <p className="text-slate-500 text-sm">No support tickets.</p>}
    </div>
  );
}
